import React, { useState, useEffect } from 'react';
import './Gallery.css';

const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Authentic Sarajevo story series based on real photos
  const portfolioItems = [
    {
      id: 9,
      category: 'tradition',
      title: 'Scars of Sarajevo',
      location: 'Photo Book · ICR 2026 Nominee',
      description: 'A photographic essay on a city that carries its wounds and its renewal in the same breath. Nominated for the International Creative Review 2026.',
      fullStory: 'Scars of Sarajevo is a photographic essay built from the streets, memorials, and faces of a city that refuses to let its history be forgotten or to be defined by it. The siege of 1992 to 1996 left marks that are still visible if you know where to look, in the pockmarked facades, the red-filled mortar craters, the hillsides of white headstones. But the same streets hold coffee rituals, music, art, and an everyday warmth that insists on the future. This book moves between those two truths, the wound and the renewal, and asks the reader to hold both at once. It is my nominated work for the International Creative Review 2026.',
      image: '/images/hillside-memorial-panorama.jpg',
      pdf: '/Scars-of-Sarajevo.pdf',
      nomination: 'Nominated · International Creative Review 2026',
      isBook: true
    },
    {
      id: 8,
      category: 'people',
      title: 'The Sketchy Collective',
      location: 'A Café in Sarajevo',
      description: 'A traveling art meetup where strangers become friends over watercolors and Bosnian coffee',
      fullStory: 'Some evenings stay with you. This one unfolded in a wood-paneled café in Sarajevo, where strangers gathered around copper trays of Bosnian coffee to do nothing but make art together. This is The Sketchy Collective, a community-driven art meetup founded by my friend Karolina, an artist from Poland who has carried these gatherings across the world, from Vietnam to Finland to this night in Sarajevo. The premise is beautifully simple: bring your sketchbook, your watercolors, and your willingness to share a table with people you have never met. What happens next is a kind of quiet magic. Pencils scratch, brushes load color from well-worn palettes, and conversation drifts between languages. Someone paints the very džezva and copper tray in front of them. Someone else loses themselves in a charcoal portrait. And at the end of the night, people hold up what they made, a little shy, a little proud. No one here is trying to be a professional. The art is just the excuse. The real work of the evening is connection, the way a blank page lowers everyone\'s defenses and turns a room of strangers into a community. Kind, talented, and inspiring people, drawn together by one artist\'s simple idea that creativity is best shared. Thank you, Karolina, for building something this good and bringing it to the road.',
      instagram: { handle: '@sketchy.collective', url: 'https://www.instagram.com/sketchy.collective' },
      images: [
        '/images/sketchy-collective-charcoal-sketch.jpg',
        '/images/sketchy-collective-watercolor-portrait.jpg',
        '/images/sketchy-collective-portrait-and-coffee.jpg',
        '/images/sketchy-collective-coffee-painting.jpg',
        '/images/sketchy-collective-charcoal-portrait.jpg',
        '/images/sketchy-collective-sketch-in-progress.jpg'
      ],
      isMultiPhoto: true
    },
    {
      id: 1,
      category: 'tradition',
      title: 'Shadows and Remembrance',
      location: 'Museums to Memorials, Sarajevo',
      description: 'From 1914 to 1996: How a city survived history\'s darkest chapters, from museum to cemetery',
      fullStory: 'This story moves from museum to memorial. It begins with the vintage automobile representing the vehicle type that carried Archduke Franz Ferdinand through these streets on June 28, 1914, before the assassination that sparked World War I. Nearly eighty years later, sniper warning signs document the siege of 1992-1996, when this same city endured 1,425 days of blockade and bombardment, the longest siege in modern warfare. High above the city, Kovači Cemetery stretches across the hillside, its white headstones marking where many siege victims rest: civilians, soldiers, and children caught in history\'s crossfire. The cemetery\'s position is deeply symbolic, the dead watching over the living city below, their sacrifice visible from the neighborhoods they died protecting.',
      images: [
        '/images/FranzFerdinandCar.jpeg',
        '/images/SniperWarningSignSarajevoSeigeMuseum.jpeg',
        '/images/hillside-memorial-panorama.jpg',
        '/images/white-stones-memorial.jpg'
      ],
      isMultiPhoto: true
    },
    {
      id: 2,
      category: 'tradition',
      title: 'The Ritual of Coffee',
      location: 'Andar Café, Sarajevo',
      description: 'Where every cup tells a story and time moves differently',
      fullStory: 'This space began as a traditional shoemaker\'s workshop, where skilled craftsmen shaped leather into footwear using techniques passed down through generations. Today, Andar Café pays homage to this artisanal heritage while serving as a sanctuary for another kind of craft: the art of traditional Bosnian coffee preparation. The transition from cobbling to coffee feels natural; both require patience, skill, and respect for time-honored methods. Here, džezva coffee is prepared with the same devotion once given to handcrafting shoes. Each cup individually brewed, served with Turkish delight and sugar cubes on copper trays. The café\'s atmosphere honors its craftsman roots while preserving the ritual of slow coffee culture.',
      images: [
        '/images/AndarBosnianCoffee2.jpeg',
        '/images/AndarCafe.jpeg'
      ],
      isMultiPhoto: true
    },
    {
      id: 3,
      category: 'tradition',
      title: 'Sweet Layers of Memory',
      location: 'Traditional Venues, Sarajevo',
      description: 'Where Ottoman recipes meet Bosnian hospitality',
      fullStory: 'Traditional Bosnian sweets represent a unique fusion of Ottoman, Central European, and local Balkan influences. Baklava, with its paper-thin phyllo layers and honey-nut filling, arrived with Ottoman settlers in the 15th century but was adapted using local walnuts and regional honey varieties. Local specialties like tufahije (stuffed apples in syrup) and hurmašice (date-shaped pastries) showcase how different culinary traditions merged into something distinctly Bosnian. These desserts aren\'t just sweets; they\'re edible history.',
      images: [
        '/images/Baklava1.jpeg',
        '/images/DesertAtAndar.jpeg',
        '/images/Baklava2.jpeg'
      ],
      isMultiPhoto: true
    },
    {
      id: 5,
      category: 'tradition',
      title: 'Cultural Crossroads',
      location: 'Sarajevo Historic Center',
      description: 'Where East meets West in stone and spirit',
      fullStory: 'Walking from the Ottoman old town to the Austro-Hungarian quarter, you traverse centuries of architectural evolution in mere blocks. Narrow old town alleys wind between 15th-century stone buildings, leading you through the traditional Baščaršija bazaar with its mosques, tekkes, and traditional craft shops with distinctive wooden overhangs. This intimate medieval streetscape flows into the late 19th-century Austro-Hungarian architecture built after 1878, when Bosnia came under Habsburg administration. European-style buildings, Catholic churches, and Habsburg administrative structures create a distinctly Central European atmosphere. The Bosnian flag, with its medieval royal symbols and EU-inspired stars, represents this layered identity. Within a short walk, you encounter Gazi Husrev-beg Mosque, the Sacred Heart Cathedral, and Orthodox churches, all part of Sarajevo\'s unique position as Europe\'s most eastern West and most western East.',
      images: [
        '/images/Child.jpeg',
        '/images/OldTownAlley.jpeg',
        '/images/OttomonDistrict.jpeg',
        '/images/EuropeanStyleBuilding.jpeg',
        '/images/PopeStatueEuropean.jpeg',
        '/images/Bosnian Flag.jpeg'
      ],
      isMultiPhoto: true
    },
    {
      id: 7,
      category: 'people',
      title: 'Music Beyond Borders',
      location: 'Park Square, Sarajevo',
      description: 'A Russian duo\'s journey through the Balkans, proving human connection transcends propaganda',
      fullStory: 'Two Russian musicians, vagabonding their way through the Balkans with nothing but their guitars and voices, set up in a Sarajevo park. As an American photographer, I was initially uncertain how our interaction might unfold. After all, our media constantly reinforces narratives of division between Russians and Americans. But within minutes of conversation, those fabricated divides crumbled completely. We talked about music, travel, dreams, and the absurdity of political propaganda that tries to make enemies of people who share so much in common. They spoke of their journey through Bosnia, their love for the Balkan spirit, and their hope to connect with people through music rather than nationality. I photographed them not as "Russians" or as an "American," but simply as humans creating beauty in a park, proving that when you meet real, genuine people (regardless of where they\'re from), the manufactured conflicts of politics and media dissolve into meaninglessness. Music, it turns out, needs no translation.',
      images: [
        '/images/russian-duo-performance.jpg',
        '/images/guitarist-in-focus.jpg',
        '/images/singer-performing.jpg',
        '/images/street-performance-intimate.jpg'
      ],
      isMultiPhoto: true
    }
  ];

  const categories = [
    { key: 'all', label: 'All Stories' },
    { key: 'people', label: 'People' },
    { key: 'tradition', label: 'Memory & Tradition' }
  ];

  const filteredItems = selectedCategory === 'all'
    ? portfolioItems
    : portfolioItems.filter(item => item.category === selectedCategory);

  const openModal = (item) => {
    setSelectedPhoto(item);
    setSelectedImageIndex(0);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  const closeModal = () => {
    setSelectedPhoto(null);
    setSelectedImageIndex(0);
    setIsModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  const navigateWithinPhoto = (direction) => {
    if (!selectedPhoto || !selectedPhoto.isMultiPhoto) return;

    setSelectedImageIndex(prevIndex => {
      if (direction === 'next') {
        return prevIndex === selectedPhoto.images.length - 1 ? 0 : prevIndex + 1;
      } else {
        return prevIndex === 0 ? selectedPhoto.images.length - 1 : prevIndex - 1;
      }
    });
  };

  // Preload every photo in the open series so clicking next/prev is
  // instant instead of waiting on a fresh network fetch each time.
  useEffect(() => {
    if (!isModalOpen || !selectedPhoto) return;

    const sources = selectedPhoto.isMultiPhoto
      ? selectedPhoto.images
      : [selectedPhoto.image];

    sources.forEach(src => {
      if (src) {
        const img = new Image();
        img.src = src;
      }
    });
  }, [isModalOpen, selectedPhoto]);

  // Keyboard navigation and cleanup
  useEffect(() => {
    if (!isModalOpen) return;

    const handleKeyPress = (e) => {
      switch (e.key) {
        case 'Escape':
          setSelectedPhoto(null);
          setIsModalOpen(false);
          document.body.style.overflow = 'unset';
          break;
        case 'ArrowLeft':
          if (selectedPhoto && selectedPhoto.isMultiPhoto) {
            navigateWithinPhoto('prev');
          }
          break;
        case 'ArrowRight':
          if (selectedPhoto && selectedPhoto.isMultiPhoto) {
            navigateWithinPhoto('next');
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isModalOpen, filteredItems]);

  return (
    <section id="gallery" className="gallery">
      <div className="gallery-content">
        <div className="gallery-header">
          <h2>The Collection</h2>
          <p>Authentic moments and genuine connections from the road, where every stranger has a story worth telling.</p>
        </div>

        <div className="filter-buttons">
          {categories.map(category => (
            <button
              key={category.key}
              className={selectedCategory === category.key ? 'active' : ''}
              onClick={() => setSelectedCategory(category.key)}
            >
              {category.label}
            </button>
          ))}
        </div>

        <div className="gallery-grid">
          {filteredItems.map(item => (
            <div key={item.id} className="gallery-item" onClick={() => openModal(item)}>
              <div className="image-placeholder">
                <img
                  src={item.isMultiPhoto ? item.images[0] : item.image}
                  alt={`${item.title} - ${item.location}`}
                  loading="lazy"
                />
                {item.isMultiPhoto && (
                  <div className="photo-series-indicator">
                    <span>{item.images.length} photos</span>
                  </div>
                )}
                {item.isBook && (
                  <div className="photo-series-indicator book-indicator">
                    <span>Photo Book · PDF</span>
                  </div>
                )}
                <div className="placeholder-content">
                  <span className="image-title">{item.title}</span>
                  <span className="image-location">{item.location}</span>
                </div>
                <div className="view-story-overlay">
                  <span>{item.isBook ? 'View Photo Book' : item.isMultiPhoto ? `View ${item.images.length} Photo Series` : 'View Story'}</span>
                </div>
              </div>
              <div className="item-info">
                <h3>{item.title}</h3>
                <p className="location">{item.location}</p>
                <p className="description">{item.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Photo Modal */}
        {isModalOpen && selectedPhoto && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={closeModal}>×</button>

              <div className="modal-image">
                <img
                  src={selectedPhoto.isMultiPhoto ? selectedPhoto.images[selectedImageIndex] : selectedPhoto.image}
                  alt={`${selectedPhoto.title} - ${selectedPhoto.location}${selectedPhoto.isMultiPhoto ? ` (${selectedImageIndex + 1}/${selectedPhoto.images.length})` : ''}`}
                />
                {selectedPhoto.isMultiPhoto && (
                  <div className="image-counter">
                    {selectedImageIndex + 1} / {selectedPhoto.images.length}
                  </div>
                )}
                {selectedPhoto.isMultiPhoto && selectedPhoto.images.length > 1 && (
                  <>
                    <button className="within-nav within-prev" onClick={() => navigateWithinPhoto('prev')}>‹</button>
                    <button className="within-nav within-next" onClick={() => navigateWithinPhoto('next')}>›</button>
                  </>
                )}
              </div>

              <div className="modal-story">
                <h2>{selectedPhoto.title}</h2>
                <p className="modal-location">{selectedPhoto.location}</p>
                {selectedPhoto.nomination && (
                  <p className="modal-nomination">{selectedPhoto.nomination}</p>
                )}
                <p className="modal-full-story">{selectedPhoto.fullStory}</p>
                {selectedPhoto.pdf && (
                  <a
                    className="modal-book-link"
                    href={selectedPhoto.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open the Photo Book (PDF)
                  </a>
                )}
                {selectedPhoto.instagram && (
                  <p className="modal-instagram">
                    Follow along:{' '}
                    <a
                      href={selectedPhoto.instagram.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selectedPhoto.instagram.handle}
                    </a>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;